import React, { useEffect, useState, useCallback } from 'react';
import { styled } from '@compiled/react';
import Heading from '@atlaskit/heading';
import { Box, Stack, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { getTokenValue, useThemeObserver } from '@atlaskit/tokens';
import { isFedRamp } from '@atlassian/atl-context';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import {
	WIDGET_FEDRAMP_SERVICE_ASSETS_BASE_URL,
	WIDGET_SERVICE_ASSETS_BASE_URL,
} from '../../../common/constants';
import type { ConfigType, RequestType } from '../../../common/types';
import { useGetFeatureFlags } from '../../../services/use-get-feature-flags';
import { useGetForms } from '../../../services/use-get-forms';
import messages from './messages';
import { objToEncodedString } from './utils';

type PreviewWrapperProps = {
	config: ConfigType | undefined;
	projectKey: string;
	projectId: number;
	baseUrl: string;
};

interface PreviewConfig extends ConfigType {
	cloudId: string;
	projectId: number;
	baseUrl: string;
	backgroundColor: string;
	flag?: { [key: string]: boolean };
	defaultRequestType: RequestType;
	analytics: {
		workspaceId: string;
		env: string;
	};
}
export const CONFIG_PREVIEW_LOADED = 'config-preview-loaded';

export const PreviewWrapper = ({ config, projectKey, projectId, baseUrl }: PreviewWrapperProps) => {
	const env = useEnvironment();
	const activationId = useActivationId();
	const [backgroundColor, setBackgroundColor] = useState(
		getTokenValue('elevation.surface', '#ffffff'),
	);
	const { colorMode } = useThemeObserver();
	const [iframeLoaded, setIframeLoaded] = useState(false);
	const { formatMessage } = useIntl();
	const [formsState, fetchForms] = useGetForms(projectKey, projectId);
	const [previewUrlWithConfig, setPreviewUrlWithConfig] = useState('');
	const [featureFlagsState, fetchFeatureFlags] = useGetFeatureFlags();
	const { cloudId } = useTenantContext();

	// Use useCallback to memoize determineBaseUrl
	const determineBaseUrl = useCallback(
		() =>
			isFedRamp()
				? WIDGET_FEDRAMP_SERVICE_ASSETS_BASE_URL[env]
				: WIDGET_SERVICE_ASSETS_BASE_URL[env],
		[env],
	);

	useEffect(() => {
		setBackgroundColor(getTokenValue('elevation.surface', '#ffffff'));
	}, [colorMode]);

	useEffect(() => {
		fetchForms();
		fetchFeatureFlags();
	}, [fetchFeatureFlags, fetchForms]);

	useEffect(() => {
		if (config?.requestTypeId && formsState.data) {
			const previewConfig: PreviewConfig = {
				...config,
				projectId,
				cloudId,
				baseUrl,
				backgroundColor,
				defaultRequestType: formsState.data[config.requestTypeId],
				analytics: {
					workspaceId: activationId,
					env,
				},
				flag: featureFlagsState.data || {},
			};

			setPreviewUrlWithConfig(
				`${determineBaseUrl()}/assets/config-preview.html#${objToEncodedString(previewConfig)}`,
			);
		}
	}, [
		config,
		backgroundColor,
		formsState.data,
		activationId,
		env,
		featureFlagsState.data,
		projectId,
		cloudId,
		baseUrl,
		determineBaseUrl,
	]);

	useEffect(() => {
		const messageListener = (e: MessageEvent) => {
			if (e.origin === determineBaseUrl() && e.data === CONFIG_PREVIEW_LOADED) {
				setIframeLoaded(true);
				window.removeEventListener('message', messageListener);
			}
		};

		window.addEventListener('message', messageListener, false);
		return () => {
			window.removeEventListener('message', messageListener);
		};
	}, [env, determineBaseUrl]);

	return (
		<Stack
			space="space.100"
			xcss={previewWrapperStyles}
			testId="servicedesk-widget-settings.ui.content.preview-wrapper"
		>
			{!iframeLoaded && (
				<Box xcss={SpinnerContainerStyles}>
					<Spinner size="large" />
				</Box>
			)}

			{formsState.data && featureFlagsState.data && previewUrlWithConfig !== '' && (
				<>
					{iframeLoaded &&
						(fg('widget_fe_virtual_agent_enabled') ? (
							<Heading size="small" as="h2">
								{formatMessage(messages.title)}
							</Heading>
						) : (
							<Heading level="h200" as="h2">
								{formatMessage(messages.title)}
							</Heading>
						))}
					<PreviewIframe
						title={formatMessage(messages.title)}
						src={previewUrlWithConfig}
						isHidden={!(iframeLoaded && config?.requestTypeId && previewUrlWithConfig !== '')}
					/>
				</>
			)}
		</Stack>
	);
};

const previewWrapperStyles = xcss({
	paddingTop: 'space.300',
});

type PreviewIframeProps = {
	isHidden: boolean;
};

const SpinnerContainerStyles = xcss({
	height: '640px',
	width: '385px',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PreviewIframe = styled.iframe({
	border: 'none',
	margin: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: (props: PreviewIframeProps) => (props.isHidden ? 0 : '640px'),
	width: '385px',
});

export default PreviewWrapper;
